import React, { useState, useEffect, Fragment } from "react";
import { Button, TextFieldGroup } from "../../common";
import { useWindowDimensions } from "../../helpers/hooks";
import { isSmallerThanDesktop } from "../../helpers";
import * as styles from "./styles.module.scss";
import FileDropzoneNew from "../../common/FileDropzoneNew";
import Hints from "./Hints";

const AddDocument = ({
  idType,
  document,
  setStep,
  frontOfIdFile,
  backOfIdFile,
  handleChange,
  step,
  hints,
  onSubmit,
}) => {
  const [hintsVisible, setHintsVisible] = useState(hints.length > 0);
  const [backToHints, setBackToHints] = useState(false);
  const [isValidFileTypeFront, setIsValidFileTypeFront] = useState(true);
  const [isValidFileTypeBack, setIsValidFileTypeBack] = useState(true);
  const [invalidShareCode, setInvalidShareCode] = useState(true);
  const [shareCodeValidationMessages, setShareCodeValidationMessages] =
    useState([]);
  const [sharecode, setShareCode] = useState("");

  const { width } = useWindowDimensions();
  const isSmallerThanLarge = isSmallerThanDesktop(width);

  const isShareCode = document && document.id && document.id === "shareCode";
  const isBackRequired = document && document.subtitles[1];

  const validateShareCode = (value) => {
    const shareCodeRegex = /^[wW][a-z0-9]{8}$/gi;
    if (!shareCodeRegex.test(value)) {
      const messages = [];
      if (!value.toLowerCase().startsWith("w")) {
        messages.push("Share code must start with 'W'");
      }
      if (value.length !== 9) {
        messages.push("Share code must be exactly 9 characters");
      }
      if (!/^[a-z0-9]*$/gi.test(value)) {
        messages.push("Share code must contain only letters and numbers");
      }
      if (messages.length === 0) {
        messages.push(
          "Share code must be 9 alphanumeric characters starting with 'W'"
        );
      }
      setShareCodeValidationMessages(messages);
      setInvalidShareCode(true);
    } else {
      setShareCodeValidationMessages([]);
      setInvalidShareCode(false);
    }
    setShareCode(value);
  };

  const handleSubmit = () => {
    setStep(step + 1);
    onSubmit();
  };

  const onImageUpload = (key, value) => {
    handleChange(key, value);
    if (isSmallerThanLarge) {
      if (key === "frontOfIdFile") {
        setHintsVisible(false);
        if (frontOfIdFile) setIsValidFileTypeFront(true);
      }
      if (key === "backOfIdFile" && backOfIdFile) {
        setIsValidFileTypeBack(true);
      }
      if (backToHints) {
        setBackToHints(false);
      }
    }
  };

  const handleBack = (fileStateName) => {
    return () => {
      if (!hintsVisible) {
        if (isShareCode) setStep(step - 1);
        else if (fileStateName === "backOfIdFile") {
          handleChange(fileStateName, null);
        } else {
          setHintsVisible(true);
          setBackToHints(true);
        }
      } else {
        setStep(step - 1);
      }
    };
  };

  useEffect(() => {
    if (!isShareCode && isSmallerThanLarge && frontOfIdFile && backOfIdFile) {
      handleChange("backOfIdFile", null);
    }
  }, [isValidFileTypeFront]);

  useEffect(() => {
    if (!invalidShareCode) {
      handleChange("shareCodeFile", sharecode);
    }
  }, [invalidShareCode]);

  useEffect(() => {
    if (!document) {
      setStep(0);
    }
  }, []);

  const linkImigrationStatus =
    "https://www.gov.uk/prove-right-to-work/get-a-share-code-online";

  return (
    <Fragment>
      <div>
        {document && document.title && (
          <div className="mb-2">{document.title}</div>
        )}
        {isBackRequired && <div className="mb-1">{document.subtitles[0]}</div>}
        {isShareCode ? (
          <Fragment>
            <div className="mb-2">
              As part of the EU Settlement Scheme, you will need to provide us
              your share code that you have been issued so we can view your
              pre-settled or settled status.
              <br />
              <strong>Important:</strong> Your share code must start with the
              letter &apos;W&apos;. If you have a share code starting with
              &apos;S&apos; or &apos;R&apos;, this is not the correct code for
              right to work verification.
              <br />
              If you do not have a share code starting with &apos;W&apos;,
              please visit:
              <a
                href={linkImigrationStatus}
                target="_blank"
                rel="noreferrer"
                className={styles.link}
              >
                {` ${linkImigrationStatus}`}
              </a>
              <br />
              If you do have a share code starting with &apos;W&apos;, please
              enter it into the box below.
            </div>
            <div>
              <label htmlFor="sharecode">Share code*</label>
              <TextFieldGroup
                value={sharecode}
                placeholder="W1234567G"
                onChange={(e) => {
                  validateShareCode(e.target.value);
                }}
                classes="register_field form-control"
                inputWidth="200"
                validationMessageIsRed
                validationMessages={shareCodeValidationMessages}
              />
            </div>
          </Fragment>
        ) : (
          <Fragment>
            <div className="mb-4">
              <FileDropzoneNew
                handleChange={onImageUpload}
                fileStateName="frontOfIdFile"
                fileParsedToString={frontOfIdFile}
                handleInvalidType={() => setIsValidFileTypeFront(false)}
                isFileValid={isValidFileTypeFront}
                wrongFileTypeHint="Your image needs to be provided as a JPEG or PNG file."
              />
            </div>
            {!isBackRequired && (
              <div className="mb-lg-4">
                Ensure the picture is recent and clear - we will not be able to
                approve your right to work documents without this document.
              </div>
            )}
          </Fragment>
        )}
        {isBackRequired && !isShareCode && (
          <Fragment>
            <div className="mb-1">{document.subtitles[1]}</div>
            <div className="mb-4">
              <FileDropzoneNew
                fileStateName="backOfIdFile"
                fileParsedToString={backOfIdFile}
                handleInvalidType={() => setIsValidFileTypeBack(false)}
                isFileValid={isValidFileTypeBack}
                handleChange={onImageUpload}
                wrongFileTypeHint="Your image needs to be provided as a JPEG or PNG file."
              />
            </div>
          </Fragment>
        )}
        {!isShareCode && (
          <div className="mb-2">
            <Hints hints={hints} />
          </div>
        )}
      </div>

      {isBackRequired && !isShareCode && (
        <div className={`mt-4 mt-lg-auto mb-2 ${styles.actions}`}>
          <Button
            classes="btn btn-outline-info text-dark mr-1"
            onClick={handleBack("backOfIdFile")}
          >
            Back
          </Button>

          <Button
            classes="btn btn-primary btn-block"
            onClick={handleSubmit}
            disabled={
              !frontOfIdFile ||
              (isBackRequired && idType !== "other" && !backOfIdFile)
            }
          >
            Next
          </Button>
        </div>
      )}

      {(isShareCode || !isBackRequired) && (
        <div className={`mt-4 mt-lg-auto mb-lg-2 ${styles.actions}`}>
          <Button
            classes="btn btn-outline-info text-dark mr-1"
            onClick={isShareCode ? handleBack("") : handleBack("frontOfIdFile")}
          >
            Back
          </Button>

          <Button
            classes="btn btn-primary btn-block"
            onClick={handleSubmit}
            disabled={isShareCode ? invalidShareCode : !frontOfIdFile}
          >
            Next
          </Button>
        </div>
      )}
    </Fragment>
  );
};

export default AddDocument;
